import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { COUNTRIES } from "../lib/countries";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { CheckCircle2, ShieldCheck, ShieldAlert, Link as LinkIcon, KeyRound, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
    AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
    AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";

export default function AccountPage() {
    const { user, setUser, refreshUser } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState(null);
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileMsg, setProfileMsg] = useState(null);
    const [profileErr, setProfileErr] = useState("");

    const [newEmail, setNewEmail] = useState("");
    const [savingEmail, setSavingEmail] = useState(false);
    const [emailMsg, setEmailMsg] = useState(null);
    const [emailErr, setEmailErr] = useState("");

    const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
    const [savingPw, setSavingPw] = useState(false);
    const [pwMsg, setPwMsg] = useState("");
    const [pwErr, setPwErr] = useState("");

    const [delPassword, setDelPassword] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [delErr, setDelErr] = useState("");

    useEffect(() => {
        if (user === false) navigate("/login");
    }, [user, navigate]);

    useEffect(() => {
        if (user && !form) {
            const c = COUNTRIES.find((x) => x.name === user.country);
            setForm({
                name: user.name || "",
                countryName: user.country || "United States",
                dial: user.dial_code || (c ? c.dial : "+1"),
                phone: user.phone || "",
                consent_transactional: !!user.consent_transactional,
                consent_marketing: !!user.consent_marketing,
            });
        }
    }, [user, form]);

    if (!user || !form) {
        return <div className="max-w-2xl mx-auto px-4 py-24 text-center"><p className="font-mono text-sm text-muted-foreground animate-pulse">Loading your account…</p></div>;
    }

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const onCountry = (name) => {
        const c = COUNTRIES.find((x) => x.name === name);
        setForm((f) => ({ ...f, countryName: name, dial: c ? c.dial : f.dial }));
    };

    const saveProfile = async (e) => {
        e.preventDefault();
        setProfileErr(""); setProfileMsg(null); setSavingProfile(true);
        try {
            const { data } = await api.put("/auth/profile", {
                name: form.name,
                country: form.countryName,
                dial_code: form.dial,
                phone: form.phone,
                consent_transactional: form.consent_transactional,
                consent_marketing: form.consent_marketing,
            });
            setUser(data);
            setProfileMsg("Your preferences have been saved.");
        } catch (err) {
            setProfileErr(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSavingProfile(false);
        }
    };

    const saveEmail = async (e) => {
        e.preventDefault();
        setEmailErr(""); setEmailMsg(null); setSavingEmail(true);
        try {
            const { data } = await api.post("/auth/change-email", { email: newEmail });
            setUser(data);
            setEmailMsg({ text: "Email updated. Please confirm it to re-activate your subscription.", dev: data.dev_verify_url });
            setNewEmail("");
        } catch (err) {
            setEmailErr(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSavingEmail(false);
        }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        setPwErr(""); setPwMsg("");
        if (pw.next !== pw.confirm) { setPwErr("New passwords do not match."); return; }
        setSavingPw(true);
        try {
            const { data } = await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
            setPwMsg(data.message);
            setPw({ current: "", next: "", confirm: "" });
        } catch (err) {
            setPwErr(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSavingPw(false);
        }
    };

    const deleteAccount = async () => {
        setDelErr(""); setDeleting(true);
        try {
            await api.post("/auth/delete-account", { password: delPassword });
            setUser(false);
            navigate("/");
        } catch (err) {
            setDelErr(formatApiError(err.response?.data?.detail) || err.message);
            setDeleting(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="account-page">
            <div className="border-b-2 border-ink pb-4 mb-8">
                <span className="font-mono text-xs uppercase tracking-widest text-accent">Your account</span>
                <h1 className="font-serif text-4xl font-black mt-1">Account settings</h1>
            </div>

            {/* Verification status */}
            <div className={`flex items-center gap-3 border p-4 mb-8 ${user.email_verified ? "border-pos" : "border-neg"}`} data-testid="account-verify-status">
                {user.email_verified ? <ShieldCheck className="text-pos" size={20} /> : <ShieldAlert className="text-neg" size={20} />}
                <span className="text-sm">
                    {user.email_verified
                        ? <>Your email <span className="font-mono">{user.email}</span> is confirmed.</>
                        : <>Your email <span className="font-mono">{user.email}</span> is not confirmed yet. You won't receive newsletters until you confirm it.</>}
                </span>
            </div>

            {/* Profile & preferences */}
            <section className="border-2 border-ink p-6 mb-8">
                <h2 className="font-serif text-2xl font-bold mb-5">Profile &amp; contact preferences</h2>
                <form onSubmit={saveProfile} className="space-y-5">
                    <div>
                        <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Full name</label>
                        <input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="account-name"
                            className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                    </div>
                    <div>
                        <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Country</label>
                        <Select value={form.countryName} onValueChange={onCountry}>
                            <SelectTrigger data-testid="account-country" className="w-full border border-ink rounded-none px-3 py-2.5 text-sm h-auto focus:ring-1 focus:ring-accent">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none border-2 border-ink max-h-72">
                                {COUNTRIES.map((c) => (
                                    <SelectItem key={c.code} value={c.name} className="rounded-none font-sans text-sm">
                                        {c.name} <span className="font-mono text-xs text-muted-foreground">({c.dial})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Phone number</label>
                        <div className="flex">
                            <span className="border border-r-0 border-ink px-3 flex items-center font-mono text-sm bg-muted" data-testid="account-dial">{form.dial}</span>
                            <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="account-phone"
                                className="flex-1 border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent font-mono" />
                        </div>
                    </div>
                    <div className="space-y-3 border-t border-hair pt-5">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <Checkbox checked={form.consent_transactional} onCheckedChange={(v) => set("consent_transactional", !!v)} data-testid="account-consent-transactional" className="rounded-none border-ink mt-0.5" />
                            <span className="text-sm leading-snug text-muted-foreground">Receive transactional messages (account &amp; service notifications) by email and SMS.</span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <Checkbox checked={form.consent_marketing} onCheckedChange={(v) => set("consent_marketing", !!v)} data-testid="account-consent-marketing" className="rounded-none border-ink mt-0.5" />
                            <span className="text-sm leading-snug text-muted-foreground">Receive marketing messages (newsletters &amp; offers) by email and SMS.</span>
                        </label>
                        {!user.email_verified && form.consent_marketing && (
                            <p className="text-[11px] text-neg">Marketing delivery starts once your email is confirmed.</p>
                        )}
                    </div>
                    {profileErr && <p className="text-sm text-neg border border-neg p-3" data-testid="account-profile-error">{profileErr}</p>}
                    {profileMsg && (
                        <div className="flex items-center gap-2 border border-pos p-3" data-testid="account-profile-success">
                            <CheckCircle2 className="text-pos" size={18} /><span className="text-sm">{profileMsg}</span>
                        </div>
                    )}
                    <button type="submit" disabled={savingProfile} data-testid="account-save-profile"
                        className="w-full bg-ink text-white py-3 font-mono text-xs uppercase tracking-widest hover:bg-accent transition-colors duration-150 disabled:opacity-50">
                        {savingProfile ? "Saving…" : "Save preferences"}
                    </button>
                </form>
            </section>

            {/* Email address */}
            <section className="border-2 border-ink p-6">
                <h2 className="font-serif text-2xl font-bold mb-1">Email address</h2>
                <p className="text-sm text-muted-foreground mb-5">Changing your email requires re-confirming the new address before newsletters resume.</p>
                <form onSubmit={saveEmail} className="space-y-4">
                    <div>
                        <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">New email</label>
                        <input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder={user.email} data-testid="account-new-email"
                            className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                    </div>
                    {emailErr && <p className="text-sm text-neg border border-neg p-3" data-testid="account-email-error">{emailErr}</p>}
                    {emailMsg && (
                        <div className="border border-pos p-3" data-testid="account-email-success">
                            <div className="flex items-center gap-2"><CheckCircle2 className="text-pos" size={18} /><span className="text-sm">{emailMsg.text}</span></div>
                            {emailMsg.dev && (
                                <Link to={emailMsg.dev.replace(/^https?:\/\/[^/]+/, "")} data-testid="account-dev-verify-link" className="mt-2 inline-flex items-center gap-1 text-sm text-accent link-underline break-all">
                                    <LinkIcon size={13} /> Confirm your new email now →
                                </Link>
                            )}
                        </div>
                    )}
                    <button type="submit" disabled={savingEmail} data-testid="account-save-email"
                        className="w-full border-2 border-ink py-3 font-mono text-xs uppercase tracking-widest hover:bg-ink hover:text-white transition-colors duration-150 disabled:opacity-50">
                        {savingEmail ? "Updating…" : "Update email"}
                    </button>
                </form>
            </section>

            {/* Change password */}
            <section className="border-2 border-ink p-6 mt-8">
                <h2 className="font-serif text-2xl font-bold mb-1 flex items-center gap-2"><KeyRound size={20} /> Change password</h2>
                <p className="text-sm text-muted-foreground mb-5">Choose a strong new password for signing in.</p>
                <form onSubmit={savePassword} className="space-y-4">
                    <div>
                        <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Current password</label>
                        <input type="password" required value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} data-testid="account-current-password"
                            className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">New password</label>
                            <input type="password" required minLength={6} value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} data-testid="account-new-password"
                                className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                        </div>
                        <div>
                            <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Confirm new</label>
                            <input type="password" required minLength={6} value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} data-testid="account-confirm-password"
                                className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                        </div>
                    </div>
                    {pwErr && <p className="text-sm text-neg border border-neg p-3" data-testid="account-password-error">{pwErr}</p>}
                    {pwMsg && (
                        <div className="flex items-center gap-2 border border-pos p-3" data-testid="account-password-success">
                            <CheckCircle2 className="text-pos" size={18} /><span className="text-sm">{pwMsg}</span>
                        </div>
                    )}
                    <button type="submit" disabled={savingPw} data-testid="account-save-password"
                        className="w-full bg-ink text-white py-3 font-mono text-xs uppercase tracking-widest hover:bg-accent transition-colors duration-150 disabled:opacity-50">
                        {savingPw ? "Updating…" : "Update password"}
                    </button>
                </form>
            </section>

            {/* Danger zone */}
            {user.role !== "admin" && (
                <section className="border-2 border-neg p-6 mt-8" data-testid="account-danger-zone">
                    <h2 className="font-serif text-2xl font-bold mb-1 flex items-center gap-2 text-neg"><Trash2 size={20} /> Delete account</h2>
                    <p className="text-sm text-muted-foreground mb-5">This permanently removes your account, profile and marketing preferences. This cannot be undone.</p>
                    <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Confirm your password</label>
                    <input type="password" value={delPassword} onChange={(e) => setDelPassword(e.target.value)} data-testid="account-delete-password"
                        className="w-full border border-neg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-neg mb-4" />
                    {delErr && <p className="text-sm text-neg border border-neg p-3 mb-4" data-testid="account-delete-error">{delErr}</p>}
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <button type="button" disabled={!delPassword || deleting} data-testid="account-delete-trigger"
                                className="w-full bg-neg text-white py-3 font-mono text-xs uppercase tracking-widest hover:bg-ink transition-colors duration-150 disabled:opacity-50">
                                {deleting ? "Deleting…" : "Delete my account"}
                            </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="border-2 border-ink rounded-none bg-white" data-testid="account-delete-dialog">
                            <AlertDialogHeader>
                                <AlertDialogTitle className="font-serif text-2xl">Delete your account?</AlertDialogTitle>
                                <AlertDialogDescription className="text-sm">
                                    This will permanently delete your account and all associated data. You can't undo this.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-none border-ink font-mono text-xs uppercase tracking-wider" data-testid="account-delete-cancel">Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={deleteAccount} data-testid="account-delete-confirm"
                                    className="rounded-none bg-neg text-white font-mono text-xs uppercase tracking-wider hover:bg-ink">
                                    Yes, delete everything
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </section>
            )}
        </div>
    );
}
